import { useState } from "react";

export interface FilterState {
  status: string;
  level: string;
  language: string;
  sort: string;
}

interface FilterBarProps {
  filters: FilterState;
  onChange: (next: Partial<FilterState>) => void;
  onReset: () => void;
}

const statusOptions = [
  { value: "all", label: "全部状态" },
  { value: "live", label: "直播中" },
  { value: "offline", label: "离线" },
];

const levelOptions = [
  { value: "all", label: "全部等级" },
  { value: "签约", label: "签约主播" },
  { value: "普通", label: "普通主播" },
];

const languageOptions = [
  { value: "all", label: "全部语言" },
  { value: "中文", label: "中文" },
  { value: "英语", label: "英语" },
  { value: "日语", label: "日语" },
  { value: "韩语", label: "韩语" },
];

const sortOptions = [
  { value: "latest", label: "最新开播" },
  { value: "popularity", label: "人气最高" },
  { value: "duration", label: "直播时长" },
];

function ChipGroup({
  label,
  options,
  value,
  onSelect,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs text-foreground-400">{label}</span>
      <div className="flex flex-wrap items-center gap-1.5">
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onSelect(option.value)}
              className={`rounded-full px-3 py-1 text-xs transition-colors whitespace-nowrap ${
                active
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-500 hover:text-foreground-900"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({ filters, onChange, onReset }: FilterBarProps) {
  const [panelOpen, setPanelOpen] = useState(false);

  const activeCount = [
    filters.status !== "all",
    filters.level !== "all",
    filters.language !== "all",
  ].filter(Boolean).length;

  return (
    <>
      <div className="hidden rounded-lg border border-background-200/70 bg-background-100 p-4 md:block">
        <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
          <ChipGroup label="直播状态" options={statusOptions} value={filters.status} onSelect={(v) => onChange({ status: v })} />
          <ChipGroup label="主播等级" options={levelOptions} value={filters.level} onSelect={(v) => onChange({ level: v })} />
          <ChipGroup
            label="直播语言"
            options={languageOptions}
            value={filters.language}
            onSelect={(v) => onChange({ language: v })}
          />
          <ChipGroup label="排序方式" options={sortOptions} value={filters.sort} onSelect={(v) => onChange({ sort: v })} />
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-500 transition-colors hover:border-primary-400 hover:text-primary-500 whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-refresh-line"></i>
            </span>
            重置筛选
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg border border-background-200/70 bg-background-100 p-3 md:hidden">
        <span className="text-sm text-foreground-600">
          当前排序：
          <span className="text-primary-500">
            {sortOptions.find((option) => option.value === filters.sort)?.label}
          </span>
        </span>
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          className="flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-1.5 text-xs text-background-50 whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-filter-3-line"></i>
          </span>
          筛选{activeCount > 0 ? `（${activeCount}）` : ""}
        </button>
      </div>

      {panelOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-background-50/70" onClick={() => setPanelOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85%] overflow-y-auto border-l border-background-200 bg-background-50 p-5">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-heading text-base font-semibold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-500"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="space-y-5">
              <ChipGroup label="直播状态" options={statusOptions} value={filters.status} onSelect={(v) => onChange({ status: v })} />
              <ChipGroup label="主播等级" options={levelOptions} value={filters.level} onSelect={(v) => onChange({ level: v })} />
              <ChipGroup
                label="直播语言"
                options={languageOptions}
                value={filters.language}
                onSelect={(v) => onChange({ language: v })}
              />
              <ChipGroup label="排序方式" options={sortOptions} value={filters.sort} onSelect={(v) => onChange({ sort: v })} />
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={onReset}
                className="flex-1 rounded-md border border-background-300 py-2.5 text-sm text-foreground-600 whitespace-nowrap"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="flex-1 rounded-md bg-primary-500 py-2.5 text-sm text-background-50 whitespace-nowrap"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}